import type { EItemType, FileForProcessing, ISamplePool } from '../../../../Model/index.js'
import type { MatchedRow, MergedOperation } from './CSVModalTypes.js'
import { checkControlRule, normalizePoolValue } from '../../../../Util/sheetItemRoles/index.ts'
import { poolDisplayName } from '../../OrderManagementPage/PoolName.tsx'

/**
 * What a sheet's type and pool columns do to an order's structure — pools created, named and filled, items
 * moved and converted — and whether that needs a second look before it is applied.
 */

export const NEW_POOL_REF_PREFIX = 'new-pool:'

export type PoolCreate = { ref: string; key: string; label?: string }
export type PoolRename = { poolId: string; from?: string; to: string; version?: string }
export type ItemMove = { itemId: string; fromPoolId: string; toPool: string; itemType: EItemType }
export type TypeChange = { itemId: string; from: EItemType; to: EItemType }
export type ReplicaPlacement = { toPool: string; itemType: EItemType; fileKey: string }

export type ResultPool = { ref: string; name: string; existedWithItems: boolean; items: { fileKey: string; itemType: EItemType }[] }

export type SheetStructurePlan = {
  creates: PoolCreate[]
  renames: PoolRename[]
  moves: ItemMove[]
  typeChanges: TypeChange[]
  replicas: ReplicaPlacement[]
  /** Sheet pool key → existing pool id, or a {@link NEW_POOL_REF_PREFIX} ref for a pool still to create. */
  poolForKey: Record<string, string>
  /** The order's pools as they would be after the import. */
  result: ResultPool[]
}

export type StructureOptions = {
  /** Whether the sheet's pool values may name pools; when not, created pools stay unnamed. */
  useNames: boolean
}

/** Copies of one control share its files, so a file key identifies a control across pools. */
export const fileKeyOf = (item: Pick<FileForProcessing, 'forwardFileId' | 'reverseFileId'>) =>
  `${item.forwardFileId}|${item.reverseFileId ?? ''}`

const itemsOf = (pool: ISamplePool): FileForProcessing[] => [...pool.samples, ...pool.negativeControls]

type SheetGroup = { key: string; name?: string; itemIds: string[]; order: number }

export const planSheetStructure = (pools: ISamplePool[], matched: MatchedRow[], options: StructureOptions): SheetStructurePlan => {
  const primary = matched.filter((entry) => !entry.replica)
  const groups = new Map<string, SheetGroup>()
  matched.forEach((entry) => {
    const pool = entry.row.pool
    if (!pool) {
      return
    }
    const group = groups.get(pool.key) ?? { key: pool.key, name: pool.name, itemIds: [], order: groups.size }
    if (!entry.replica) {
      group.itemIds.push(entry.item.id)
    }
    groups.set(pool.key, group)
  })

  const poolForKey: Record<string, string> = {}
  const claimed = new Set<string>()
  const claimedByName = new Set<string>()
  const claim = (key: string, poolId: string) => {
    poolForKey[key] = poolId
    claimed.add(poolId)
  }

  /* a value naming an existing pool means that pool, as after an export round trip */
  groups.forEach((group) => {
    const index = pools.findIndex(
      (pool, i) =>
        !claimed.has(pool.id) &&
        (normalizePoolValue(poolDisplayName(pool, i)) === group.key || (!!pool.label && normalizePoolValue(pool.label) === group.key))
    )
    if (index >= 0) {
      claim(group.key, pools[index].id)
      claimedByName.add(group.key)
    }
  })

  /* biggest groups first keep the pool that already holds most of their items */
  const remaining = [...groups.values()]
    .filter((group) => !(group.key in poolForKey))
    .sort((a, b) => b.itemIds.length - a.itemIds.length || a.order - b.order)
  const poolOfItem = new Map(primary.map((entry) => [entry.item.id, entry.item.poolId]))
  remaining.forEach((group) => {
    const overlap = new Map<string, number>()
    group.itemIds.forEach((id) => {
      const poolId = poolOfItem.get(id)
      if (poolId && !claimed.has(poolId)) {
        overlap.set(poolId, (overlap.get(poolId) ?? 0) + 1)
      }
    })
    const best = [...overlap.entries()].sort((a, b) => b[1] - a[1])[0]
    if (best) {
      claim(group.key, best[0])
    }
  })

  const creates: PoolCreate[] = []
  remaining
    .filter((group) => !(group.key in poolForKey))
    .sort((a, b) => a.order - b.order)
    .forEach((group) => {
      const emptyPool = pools.find((pool) => !claimed.has(pool.id) && itemsOf(pool).length === 0)
      if (emptyPool) {
        claim(group.key, emptyPool.id)
        return
      }
      const ref = `${NEW_POOL_REF_PREFIX}${group.key}`
      poolForKey[group.key] = ref
      creates.push({ ref, key: group.key, label: options.useNames ? group.name : undefined })
    })

  const renames: PoolRename[] = []
  if (options.useNames) {
    groups.forEach((group) => {
      if (claimedByName.has(group.key) || !group.name) {
        return
      }
      const index = pools.findIndex((pool) => pool.id === poolForKey[group.key])
      if (index >= 0 && poolDisplayName(pools[index], index) !== group.name) {
        renames.push({ poolId: pools[index].id, from: pools[index].label, to: group.name, version: pools[index].version })
      }
    })
  }

  const moves: ItemMove[] = []
  const typeChanges: TypeChange[] = []
  primary.forEach(({ row, item }) => {
    const itemType = row.itemType ?? item.itemType
    if (row.itemType && row.itemType !== item.itemType) {
      typeChanges.push({ itemId: item.id, from: item.itemType, to: row.itemType })
    }
    const target = row.pool ? poolForKey[row.pool.key] : undefined
    if (target && item.poolId && target !== item.poolId) {
      moves.push({ itemId: item.id, fromPoolId: item.poolId, toPool: target, itemType })
    }
  })

  const replicas: ReplicaPlacement[] = matched
    .filter((entry) => entry.replica)
    .map(({ row, item }) => ({
      toPool: (row.pool ? poolForKey[row.pool.key] : undefined) ?? item.poolId ?? '',
      itemType: row.itemType ?? item.itemType,
      fileKey: fileKeyOf(item)
    }))

  const plan = { creates, renames, moves, typeChanges, replicas, poolForKey }
  return { ...plan, result: resultPools(pools, plan) }
}

const resultPools = (pools: ISamplePool[], plan: Omit<SheetStructurePlan, 'result'>): ResultPool[] => {
  const typeOf = new Map(plan.typeChanges.map((change) => [change.itemId, change.to]))
  const moveOf = new Map(plan.moves.map((move) => [move.itemId, move.toPool]))
  const renamed = new Map(plan.renames.map((rename) => [rename.poolId, rename.to]))
  const result: ResultPool[] = [
    ...pools.map((pool, index) => ({
      ref: pool.id,
      name: renamed.get(pool.id) ?? poolDisplayName(pool, index),
      existedWithItems: itemsOf(pool).length > 0,
      items: [] as ResultPool['items']
    })),
    ...plan.creates.map((create, index) => ({
      ref: create.ref,
      name: create.label ?? `Pool ${pools.length + index}`,
      existedWithItems: false,
      items: [] as ResultPool['items']
    }))
  ]
  const byRef = new Map(result.map((pool) => [pool.ref, pool]))
  pools.forEach((pool) =>
    itemsOf(pool).forEach((item) => {
      byRef.get(moveOf.get(item.id) ?? pool.id)?.items.push({ fileKey: fileKeyOf(item), itemType: typeOf.get(item.id) ?? item.itemType })
    })
  )
  plan.replicas.forEach((replica) => byRef.get(replica.toPool)?.items.push({ fileKey: replica.fileKey, itemType: replica.itemType }))
  return result
}

export const hasStructureChanges = (plan: SheetStructurePlan) =>
  plan.creates.length + plan.renames.length + plan.moves.length + plan.typeChanges.length + plan.replicas.length > 0

/* "Lots" in an order that already has structure: this many items, or a tenth of the order, whichever is more. */
const LOTS_MIN_ITEMS = 5
const LOTS_SHARE = 0.1
/* The order wizard's measured name-detection safeguard, applied to the order after the import. */
const CONTROL_SHARE_LIMIT = 0.75
const CONTROL_SHARE_MIN_ITEMS = 3
/* The sheet "contradicts many" types when it flips at least this many, and this share, of the rows it matched. */
const CONTRADICTION_MIN = 3
const CONTRADICTION_SHARE = 0.2

export type ImportAssessment = {
  hasChanges: boolean
  /** A single default pool without a name: the first import, which may do everything at once. */
  littleStructure: boolean
  confirm: boolean
  /** Why a confirmation is asked for, one sentence each. */
  reasons: string[]
  /** What the import does to pools and types, e.g. "moves 40 samples across 3 pools, creates 2 pools". */
  summary: string
}

export type AssessOptions = {
  /** Rows that matched an existing item. */
  matchedItems: number
  /** Set when the import uses a pool column; `auto` false means the user picked it against the detector. */
  poolColumn?: { auto: boolean; reason: string }
}

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`

export const describeStructure = (plan: SheetStructurePlan): string => {
  const parts: string[] = []
  if (plan.moves.length > 0) {
    const samples = plan.moves.filter((move) => move.itemType === 'SAMPLE').length
    const controls = plan.moves.length - samples
    const what = [samples > 0 ? plural(samples, 'sample', 'samples') : '', controls > 0 ? plural(controls, 'control', 'controls') : '']
      .filter(Boolean)
      .join(' and ')
    parts.push(`moves ${what} across ${plural(new Set(plan.moves.map((move) => move.toPool)).size, 'pool', 'pools')}`)
  }
  if (plan.creates.length > 0) {
    parts.push(`creates ${plural(plan.creates.length, 'pool', 'pools')}`)
  }
  if (plan.renames.length > 0) {
    parts.push(`renames ${plural(plan.renames.length, 'pool', 'pools')}`)
  }
  const toControl = plan.typeChanges.filter((change) => change.to === 'NEGATIVE_CONTROL').length
  const toSample = plan.typeChanges.length - toControl
  if (plan.typeChanges.length > 0) {
    const turns = [
      toControl > 0 ? `${plural(toControl, 'sample', 'samples')} into ${toControl === 1 ? 'a control' : 'controls'}` : '',
      toSample > 0 ? `${plural(toSample, 'control', 'controls')} into ${toSample === 1 ? 'a sample' : 'samples'}` : ''
    ]
      .filter(Boolean)
      .join(' and ')
    parts.push(`turns ${turns}`)
  }
  if (plan.replicas.length > 0) {
    parts.push(`adds ${plural(plan.replicas.length, 'copy', 'copies')} of shared items`)
  }
  return parts.join(', ')
}

export const assessSheetStructure = (pools: ISamplePool[], plan: SheetStructurePlan, options: AssessOptions): ImportAssessment => {
  const allItems = pools.flatMap(itemsOf)
  const hadControls = allItems.some((item) => item.itemType === 'NEGATIVE_CONTROL')
  /* types the sheet contradicts are judged on their own below, so controls already set do not count as structure */
  const littleStructure = pools.filter((pool) => itemsOf(pool).length > 0).length <= 1 && !pools.some((pool) => pool.label)
  const hasChanges = hasStructureChanges(plan)
  const reasons: string[] = []

  if (hasChanges && !littleStructure) {
    const lots = Math.max(LOTS_MIN_ITEMS, Math.ceil(LOTS_SHARE * allItems.length))
    if (plan.creates.length > 0) {
      reasons.push(`It creates ${plural(plan.creates.length, 'pool', 'pools')} in an order that already has its pools set up.`)
    }
    if (plan.moves.length >= lots) {
      reasons.push(`It moves ${plural(plan.moves.length, 'item', 'items')} between pools.`)
    }
    if (plan.typeChanges.length >= lots) {
      reasons.push(`It changes the type of ${plural(plan.typeChanges.length, 'item', 'items')}.`)
    }
    const relabelled = plan.renames.filter((rename) => rename.from)
    if (relabelled.length > 0) {
      reasons.push(`It renames ${relabelled.map((rename) => `"${rename.from}" to "${rename.to}"`).join(', ')}.`)
    }
  }

  const after = plan.result.flatMap((pool) => pool.items)
  const controlsAfter = after.filter((item) => item.itemType === 'NEGATIVE_CONTROL').length
  if (
    plan.typeChanges.some((change) => change.to === 'NEGATIVE_CONTROL') &&
    after.length >= CONTROL_SHARE_MIN_ITEMS &&
    controlsAfter / after.length > CONTROL_SHARE_LIMIT
  ) {
    reasons.push(`${Math.round((100 * controlsAfter) / after.length)}% of the order would be negative controls.`)
  }

  const emptied = plan.result.filter((pool) => pool.existedWithItems && pool.items.length === 0)
  if (emptied.length > 0) {
    reasons.push(`${emptied.map((pool) => `"${pool.name}"`).join(', ')} would be left empty (it is kept, not deleted).`)
  }

  const filled = plan.result.filter((pool) => pool.items.length > 0)
  if (hasChanges && filled.length > 1) {
    const rule = checkControlRule(
      filled.map((pool) => ({
        key: pool.name,
        controls: pool.items.filter((item) => item.itemType === 'NEGATIVE_CONTROL').map((item) => item.fileKey)
      }))
    )
    if (rule.withoutControls.length > 1) {
      reasons.push(`${rule.withoutControls.map((name) => `"${name}"`).join(', ')} would have no negative control; at most one pool may.`)
    }
    rule.identicalSets.forEach((names) =>
      reasons.push(`${names.map((name) => `"${name}"`).join(' and ')} would have exactly the same negative controls.`)
    )
  }

  if (
    hadControls &&
    plan.typeChanges.length >= CONTRADICTION_MIN &&
    plan.typeChanges.length >= CONTRADICTION_SHARE * options.matchedItems
  ) {
    reasons.push(`The sheet contradicts ${plural(plan.typeChanges.length, 'type', 'types')} already set on this order.`)
  }

  if (options.poolColumn && !options.poolColumn.auto && (plan.moves.length > 0 || plan.creates.length > 0)) {
    reasons.push(`The pool column was picked by hand, though: ${options.poolColumn.reason}`)
  }

  return { hasChanges, littleStructure, confirm: reasons.length > 0, reasons, summary: describeStructure(plan) }
}

/**
 * Points the planned writes at where the items are once pools exist and moves are done: a moved item is
 * written in its new pool and at the version the move returned, since PUT /contents only writes an item
 * whose body names the pool it is in. Copies go to their sheet pool.
 */
export const rebaseOperations = (
  ops: MergedOperation[],
  plan: SheetStructurePlan,
  poolIdOfRef: ReadonlyMap<string, string>,
  moved: ReadonlyMap<string, FileForProcessing>
): MergedOperation[] => {
  const resolve = (poolId: string) => poolIdOfRef.get(poolId) ?? poolId
  return ops.map((op) => {
    switch (op.op) {
      case 'UpdateSample': {
        const item = moved.get(op.sample.id)
        return item?.poolId ? { ...op, poolId: item.poolId, sample: { ...op.sample, poolId: item.poolId, version: item.version } } : op
      }
      case 'UpdateMetadata':
      case 'CreateMetadata': {
        const item = op.metadata.sampleId ? moved.get(op.metadata.sampleId) : undefined
        return item?.poolId ? { ...op, poolId: item.poolId } : op
      }
      case 'CreateSample':
        return op.poolKey && plan.poolForKey[op.poolKey] ? { ...op, poolId: resolve(plan.poolForKey[op.poolKey]) } : op
      default:
        return op
    }
  })
}
